import CloseIcon from "@mui/icons-material/Close";
import { Dialog, IconButton } from "@mui/material";
import classNames from "classnames";

import {
  DEFAULT_TIMESTAMP_FORMAT,
  TIMESTAMP_FORMATS,
  TIMESTAMP_FORMAT_DESCRIPTIONS,
  TIMESTAMP_FORMAT_LABELS,
  formatTimestamp,
  useTimestampFormat,
} from "@/shared/components/RobotoMarkdown/timeFormat";
import { Button } from "@/shared/foundation/primitives";

import styles from "./TimeFormatSettingsModal.module.css";

interface TimeFormatSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

/** Sample ns used for the in-modal preview — a realistic recent wall-clock
 *  instant so every format produces a legible example. */
const SAMPLE_NS = 1771481251468476035n;

export function TimeFormatSettingsModal({
  open,
  onClose,
}: TimeFormatSettingsModalProps) {
  const ctx = useTimestampFormat();
  // Match the fallback pattern used by FormattedTimestampText.tsx and
  // RobotoMarkdownLink.tsx so the modal stays functional even if the provider
  // is temporarily missing from the tree.
  const current = ctx?.format ?? DEFAULT_TIMESTAMP_FORMAT;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>Time format</h2>
          <p className={styles.subtitle}>
            Choose how timestamps in AI responses are displayed.
          </p>
        </div>
        <IconButton
          aria-label="Close time format settings"
          onClick={onClose}
          size="small"
          className={styles.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div
        className={styles.body}
        role="radiogroup"
        aria-label="Timestamp display format"
      >
        {TIMESTAMP_FORMATS.map((format) => {
          const selected = current === format;
          const preview = formatTimestamp(SAMPLE_NS, format);
          return (
            <button
              key={format}
              type="button"
              role="radio"
              aria-checked={selected}
              className={classNames(styles.option, {
                [styles.optionSelected]: selected,
              })}
              onClick={() => ctx?.setFormat(format)}
            >
              <span
                aria-hidden="true"
                className={classNames(styles.radio, {
                  [styles.radioSelected]: selected,
                })}
              />
              <span className={styles.optionText}>
                <span className={styles.label}>
                  {TIMESTAMP_FORMAT_LABELS[format]}
                  <span className={styles.preview}>{preview}</span>
                </span>
                <span className={styles.description}>
                  {TIMESTAMP_FORMAT_DESCRIPTIONS[format]}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className={styles.footer}>
        <Button variant="contained" onClick={onClose} size="small">
          Done
        </Button>
      </div>
    </Dialog>
  );
}
